'use client';
import React, { useState, createElement, useEffect } from 'react';
import Image from '@/components/ui/MediaImage';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { Navigation, Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { transformMediaUrl } from '@/lib/media-url-transformer';
import { shouldEmbedBlockPropsInDom } from '@/lib/embed-block-props-dom';
import type { PageUrlMap } from '@/types/blog';
import { UrlBuilder } from '@/lib/url-builder';
import {
  appendMediaProxySize,
  DEFAULT_HERO_SSR_SIZE,
  pickResponsiveHeroSize,
  type MediaProxySize,
} from '@/lib/media-proxy-size';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';

// Animasyon variants - basitleştirilmiş
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.08,
            delayChildren: 0,
        },
    },
};

const descriptionVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: { 
        opacity: 1, 
        y: 0, 
        scale: 1,
        transition: { duration: 0.7, ease: "easeOut" as const }
    }
};

const titleVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
        opacity: 1, 
        y: 0,
        transition: { duration: 0.8, ease: "easeOut" as const }
    }
};

const wordVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: {
            delay: i * 0.08,
            duration: 0.5,
            ease: "easeOut" as const
        }
    })
};

export interface HeroFeatureItem {
    text: string;
    /** Medya proxy pathname (filename) – block_json'da pathname ile kaydedilir */
    iconPathname?: string;
    iconMediaId?: number;
    iconUrl?: string;
}

interface CarouselSlide {
    id?: string;
    /** Medya proxy pathname (filename) – block_json'da pathname ile kaydedilir, id kullanılmaz */
    pathname?: string;
    mediaId?: number;
    src: string;
    sources?: Record<string, string>;
    alt: string;
    title: string;
    description: string;
    highlightWord?: string;
    element?: string;
    link?: {
        type: 'page' | 'product' | 'category' | 'custom' | any; 
        value: string | number;
        label?: string;
        slug?: string;
        url?: string;
        templateKey?: string;
    };
    order?: number;
    features?: HeroFeatureItem[];
}

// Backend sıkıştırılmış varyantlar (webp/avif) önce; ağır xlarge son çare
const mediaSourcePreference = ['webp', 'avif', 'large', 'medium', 'xlarge', 'original', 'default', 'small', 'thumb'];

const isAbsoluteUrl = (value?: string): value is string =>
    typeof value === 'string' && /^https?:\/\//i.test(value);

const normalizePath = (value?: string): string | undefined => {
    if (!value || typeof value !== 'string') return undefined;
    if (isAbsoluteUrl(value)) return value;
    if (value === '/') return '/';
    const stringValue: string = value;
    return stringValue.startsWith('/') ? stringValue : `/${stringValue}`;
};

/** Trait panelden veya API'den gelen tam URL'yi pathname'e çevirir; böylece resim aynı origin'den yüklenir. */
const toMediaPathname = (url: string): string => {
    if (!url || !url.startsWith('http')) return url;
    try {
        const parsed = new URL(url);
        if (parsed.pathname.startsWith('/api/media/proxy/')) {
            return parsed.pathname + (parsed.search || '');
        }
    } catch {
        // ignore
    }
    return url;
};

/** Tam slayt alanı linki: modalda «Link başlığı» veya link.label; yoksa görünen başlıklar */
function getSlideLinkAccessibleName(slide: CarouselSlide): string {
    const lbl = slide.link?.label;
    if (typeof lbl === 'string' && lbl.trim()) return lbl.trim();
    if (slide.description?.trim()) return slide.description.trim();
    if (slide.title?.trim()) return slide.title.trim();
    if (slide.alt?.trim()) return slide.alt.trim();
    return 'Slide';
}

/** Görünür CTA buton metni: önce link.label, yoksa kısa varsayılan */
function getSlideCtaButtonText(slide: CarouselSlide): string {
    const lbl = slide.link?.label;
    if (typeof lbl === 'string' && lbl.trim()) return lbl.trim();
    return 'Explore';
}

const selectSlideImageSrc = (slide: CarouselSlide, size: MediaProxySize): string => {
    const resolve = (raw: string): string => {
        const normalized = toMediaPathname(raw);
        const proxyOrTransformed = normalized.startsWith('/api/media/proxy/')
            ? normalized
            : transformMediaUrl(normalized, null) || normalized;
        return withHighQualitySize(proxyOrTransformed, size);
    };

    if (slide.mediaId) {
        return withHighQualitySize(`/api/media/proxy/${slide.mediaId}`, size);
    }

    if (slide.pathname) {
        const pathSegment = slide.pathname.includes('/')
            ? slide.pathname.split('/').map((s) => encodeURIComponent(s)).join('/')
            : encodeURIComponent(slide.pathname);
        return withHighQualitySize(`/api/media/proxy/${pathSegment}`, size);
    }

    if (slide.sources) {
        for (const key of mediaSourcePreference) {
            const candidate = slide.sources[key];
            if (candidate) return resolve(candidate);
        }
        const firstValue = Object.values(slide.sources)[0];
        if (typeof firstValue === 'string' && firstValue) return resolve(firstValue);
    }
    if (slide.src) return resolve(slide.src);
    return slide.src ? resolve(slide.src) : '';
};

/** Proxy URL'inde istenen varyantı zorlar (medium path + ?size=xlarge → backend xlarge döner). */
const withHighQualitySize = (
    src: string,
    size: MediaProxySize = DEFAULT_HERO_SSR_SIZE,
): string => {
    if (!src.startsWith('/api/media/proxy/')) return src;
    return appendMediaProxySize(src, size);
};

// Ortak kelime render fonksiyonu - tekrarlayan kodu azaltmak için
const renderWord = (
    word: string, 
    index: number, 
    slideKey: string, 
    highlightWord?: string, 
    isLast?: boolean,
    isBrushStroke?: boolean
) => {
    const isHighlight = highlightWord && word.toLowerCase() === highlightWord.toLowerCase();
    const className = isHighlight 
        ? "font-display text-alsero-first inline-block font-bold" 
        : isBrushStroke 
            ? "brush-stroke inline-block font-bold"
            : "inline-block font-bold";
    
    return (
        <React.Fragment key={`${slideKey}-word-${index}`}>
            <motion.span
                custom={index}
                variants={wordVariants}
                className={className}
            >
                {word}
            </motion.span>
            {!isLast && ' '}
        </React.Fragment>
    );
};

// variant: 'small' = üstte kırmızı küçük (modal: «Küçük başlık» → description)
// 'large' = ana beyaz başlık (modal: «Büyük başlık» → title). highlightWord sadece ana başlıkta.
// Büyük başlık: Figma gibi en fazla 2 satır — önce \n ile manuel kırılım; yoksa kelimeler yarıya bölünür
// (eski 3+geri kalan kuralı ikinci satırda fazladan kırılıma yol açıyordu).
const renderTitle = (
    title: string,
    highlightWord?: string,
    element?: string,
    slideKey?: string,
    variant: 'small' | 'large' = 'large',
    dataPropPath: 'title' | 'description' = 'title'
) => {
    const trimmed = (title || '').trim();
    if (!trimmed) return null;
    const isSmall = variant === 'small';
    const titleClassName = isSmall
        ? "text-xl sm:text-2xl md:text-3xl lg:text-4xl text-alsero-first font-display font-bold leading-snug max-w-[min(100%,34rem)] mx-auto mb-1.5 sm:mb-2 md:mb-3 px-1 sm:px-2"
        : "text-white font-sans font-bold text-2xl min-[400px]:text-[1.65rem] sm:text-3xl md:text-4xl lg:text-5xl leading-[1.08] sm:leading-[1.1] max-w-[min(100%,38rem)] mx-auto text-balance tracking-tight px-2 sm:px-3";

    const renderWordLine = (
        lineWords: string[],
        lineKey: string,
        offset: number,
        lastLine: boolean
    ) => (
        <motion.div className="block" variants={titleVariants}>
            {lineWords.map((word, index) => {
                const globalIndex = offset + index;
                const isLastInLine = index === lineWords.length - 1;
                const isLastOverall = lastLine && isLastInLine;
                return renderWord(
                    word,
                    globalIndex,
                    `${slideKey}-${lineKey}`,
                    highlightWord,
                    isLastInLine,
                    isLastOverall
                );
            })}
        </motion.div>
    );

    // Alt başlık: tek blok (satır içi doğal kırılım)
    if (isSmall) {
        const words = trimmed.split(/\s+/);
        return (
            <motion.div
                variants={titleVariants}
                className={titleClassName}
                data-prop-path={dataPropPath}
                data-prop-allow-html="false"
            >
                {words.map((word, index) =>
                    renderWord(word, index, slideKey || 'title', highlightWord, index === words.length - 1)
                )}
            </motion.div>
        );
    }

    // Ana başlık: açık satır sonu — Figma’daki gibi tam iki satır kontrolü
    if (trimmed.includes('\n')) {
        const rawLines = trimmed
            .split('\n')
            .map((l) => l.trim())
            .filter(Boolean);
        return createElement(
            element || 'div',
            {
                className: titleClassName,
                'data-prop-path': dataPropPath,
                'data-prop-allow-html': 'false',
            },
            rawLines.map((line, lineIndex) => {
                const words = line.split(/\s+/);
                const offset = rawLines
                    .slice(0, lineIndex)
                    .reduce((acc, ln) => acc + ln.split(/\s+/).length, 0);
                return (
                    <React.Fragment key={`${slideKey}-nl-${lineIndex}`}>
                        {renderWordLine(
                            words,
                            `nl-${lineIndex}`,
                            offset,
                            lineIndex === rawLines.length - 1
                        )}
                    </React.Fragment>
                );
            })
        );
    }

    const words = trimmed.split(/\s+/);

    // Kısa başlıklar tek satır
    if (words.length <= 3) {
        return (
            <motion.div
                variants={titleVariants}
                className={titleClassName}
                data-prop-path={dataPropPath}
                data-prop-allow-html="false"
            >
                {words.map((word, index) =>
                    renderWord(word, index, slideKey || 'title', highlightWord, index === words.length - 1)
                )}
            </motion.div>
        );
    }

    // Uzun başlık: iki dengeli satır (Figma: üst/alt yaklaşık eşit kelime)
    const mid = Math.ceil(words.length / 2);
    const firstLineWords = words.slice(0, mid);
    const secondLineWords = words.slice(mid);

    const firstLine = renderWordLine(firstLineWords, 'first', 0, false);
    const secondLine = renderWordLine(secondLineWords, 'second', firstLineWords.length, true);

    return createElement(element || 'div', {
        className: titleClassName,
        'data-prop-path': dataPropPath,
        'data-prop-allow-html': 'false',
    }, firstLine, secondLine);
};

interface HeaderCarouselProps {
    slides?: CarouselSlide[];
    features?: HeroFeatureItem[];
    pageUrls?: PageUrlMap;
    defaultLanguage?: string;
    __pageContext?: {
        languageId?: number;
        language?: string;
        languageCode?: string;
        pageUrls?: PageUrlMap;
    };
}

function getFeatureIconSrc(feature: HeroFeatureItem): string {
    if (feature.iconPathname) {
        return `/api/media/proxy/${encodeURIComponent(feature.iconPathname)}`;
    }
    if (feature.iconUrl) {
        return feature.iconUrl.startsWith('/api/media/proxy/')
            ? feature.iconUrl
            : transformMediaUrl(feature.iconUrl, null) || feature.iconUrl;
    }
    if (feature.iconMediaId) {
        return `/api/media/proxy/${feature.iconMediaId}`;
    }
    return '';
}

export default function HeaderCarousel({ 
    slides: propSlides = [],
    features: propFeatures = [],
    pageUrls: propPageUrls,
    defaultLanguage: propDefaultLanguage = 'en',
    __pageContext
}: HeaderCarouselProps) {
    const pageUrls = propPageUrls || __pageContext?.pageUrls;
    const defaultLanguage = propDefaultLanguage;
    const language = __pageContext?.language || __pageContext?.languageCode || defaultLanguage;
    
    // resolveSlideLink fonksiyonunu component içinde tanımla (pageUrls'e erişim için)
    const resolveSlideLink = (link?: CarouselSlide['link']): string => {
        if (!link) return '#';
        const rawValue = typeof link.value === 'number' ? String(link.value) : link.value;

        if (link.type === 'custom' && rawValue) return rawValue;
        if (link.type === 'page') {
            const prioritized = normalizePath(link.url || link.slug);
            if (prioritized) return prioritized;
            if (rawValue) return `/pages/${rawValue}`;
            return '#';
        }
        
        // Product ve category link'leri için dinamik URL oluştur
        if (!pageUrls || !language) {
            if (process.env.NODE_ENV === 'development') {
                console.error('HeaderCarousel: pageUrls and language are required for URL building');
            }
            return '#';
        }
        
        // UrlBuilder kullanarak URL oluştur
        const builder = new UrlBuilder(pageUrls, language, defaultLanguage);
        
        if (link.type === 'product' && rawValue) {
            let productType = link.templateKey?.replace('-detail', '') || 'private-tours';
            if (productType === 'tour') productType = 'private-tours';
            return builder.buildProductDetailUrl(productType, rawValue);
        }
        
        if (link.type === 'category' && rawValue) {
            let productType = link.templateKey?.replace('-cat', '') || 'private-tours';
            if (productType === 'tour') productType = 'private-tours';
            return builder.buildCategoryUrl(productType, rawValue);
        }
        
        return '#';
    };
    const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [heroImageSize, setHeroImageSize] = useState<MediaProxySize>(DEFAULT_HERO_SSR_SIZE);
    const [componentSlides, setComponentSlides] = useState<CarouselSlide[]>([]);
    const [componentFeatures, setComponentFeatures] = useState<HeroFeatureItem[]>([]);

    useEffect(() => {
        const updateSize = () => {
            if (typeof window === 'undefined') return;
            setHeroImageSize(pickResponsiveHeroSize(window.innerWidth));
        };
        updateSize();
        window.addEventListener('resize', updateSize, { passive: true });
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    // Üretimde data-component-props yok; SSR props kullanılır. Editör önizlemesinde attribute ile senkron.
    useEffect(() => {
        if (!shouldEmbedBlockPropsInDom()) {
            return;
        }
        const updateFromProps = () => {
            const element = document.querySelector('[data-component-path="sections/HeaderCarousel"]');
            if (!element) {
                setComponentSlides([]);
                setComponentFeatures([]);
                return;
            }

            const propsAttr = element.getAttribute('data-component-props');
            if (!propsAttr) {
                setComponentSlides([]);
                setComponentFeatures([]);
                return;
            }

            try {
                const props = JSON.parse(propsAttr);
                if (props.slides && Array.isArray(props.slides)) {
                    const processedSlides = props.slides.map((slide: CarouselSlide) => {
                        let src = slide.src || '';
                        if (src) {
                            src = toMediaPathname(src);
                            if (!src.startsWith('/api/media/proxy/')) {
                                src = transformMediaUrl(src, null) || src;
                            }
                        }
                        if (!src && slide.pathname) {
                            const pathSegment = slide.pathname.includes('/')
                                ? slide.pathname.split('/').map((s: string) => encodeURIComponent(s)).join('/')
                                : encodeURIComponent(slide.pathname);
                            src = withHighQualitySize(`/api/media/proxy/${pathSegment}`, DEFAULT_HERO_SSR_SIZE);
                        } else if (src) {
                            src = withHighQualitySize(
                                src.startsWith('/api/media/proxy/')
                                    ? src
                                    : transformMediaUrl(src, null) || src,
                                DEFAULT_HERO_SSR_SIZE,
                            );
                        } else if (slide.mediaId) {
                            src = withHighQualitySize(`/api/media/proxy/${slide.mediaId}`, DEFAULT_HERO_SSR_SIZE);
                        }
                        let transformedSources: Record<string, string> | undefined;
                        if (slide.sources) {
                            transformedSources = {};
                            for (const [key, value] of Object.entries(slide.sources)) {
                                if (typeof value === 'string' && value) {
                                    const normalized = toMediaPathname(value);
                                    const resolved = normalized.startsWith('/api/media/proxy/')
                                        ? normalized
                                        : transformMediaUrl(normalized, null) || normalized;
                                    transformedSources[key] = withHighQualitySize(resolved, DEFAULT_HERO_SSR_SIZE);
                                }
                            }
                        }
                        return {
                            ...slide,
                            src,
                            sources: transformedSources || slide.sources,
                            alt: slide.alt || slide.title || 'Slide image'
                        };
                    });
                    setComponentSlides(processedSlides);
                } else {
                    setComponentSlides([]);
                }
                setComponentFeatures(Array.isArray(props.features) ? props.features : []);
            } catch {
                setComponentSlides([]);
                setComponentFeatures([]);
            }
        };

        updateFromProps();
        const observer = new MutationObserver(updateFromProps);
        const element = document.querySelector('[data-component-path="sections/HeaderCarousel"]');
        if (element) {
            observer.observe(element, {
                attributes: true,
                attributeFilter: ['data-component-props']
            });
        }

        return () => observer.disconnect();
    }, []);

    const slides = componentSlides.length > 0 ? componentSlides : propSlides;
    const sortedSlides = [...slides].sort((a, b) => (a.order || 0) - (b.order || 0));
    const fallbackFeatures = componentFeatures.length > 0 ? componentFeatures : propFeatures;

    /** Swiper loop: en az slidesPerView + loopedSlides (≈2) slayt gerekir; tek slaytta loop uyarı verir ve bozulur. */
    const enableLoop = sortedSlides.length >= 2;

    const dataComponentProps = React.useMemo(
        () => JSON.stringify({ slides: propSlides, features: propFeatures }),
        [propSlides, propFeatures]
    );
    const embedDomProps = shouldEmbedBlockPropsInDom();

    return (
        <section
            className="relative isolate z-10 h-[min(88svh,460px)] w-full min-h-[300px] bg-[#1a1a1a] sm:h-[min(85svh,520px)] md:h-[min(82svh,620px)] lg:h-[750px] overflow-hidden"
            {...(embedDomProps
                ? {
                      'data-component-path': 'sections/HeaderCarousel',
                      'data-component-props': dataComponentProps,
                  }
                : {})}
        >
            <Swiper
                modules={[Navigation, Autoplay]}
                spaceBetween={0}
                slidesPerView={1}
                loop={enableLoop}
                rewind={!enableLoop && sortedSlides.length > 1}
                autoplay={{ delay: 5000, disableOnInteraction: false }}
                onSwiper={setSwiperInstance}
                onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
                className="relative z-0 h-full w-full touch-pan-y [&_.swiper-slide]:pointer-events-none"
            >
                {sortedSlides.length === 0 ? (
                    <SwiperSlide key="empty">
                        <div className="relative w-full h-full flex items-center justify-center bg-gray-100">
                            <div className="text-center text-gray-400">
                                <p className="text-lg">Henüz slide eklenmemiş</p>
                                <p className="text-sm mt-2">Sayfa editöründe component'i seçin ve "Slides Yönet" butonunu kullanın</p>
                            </div>
                        </div>
                    </SwiperSlide>
                ) : (
                    sortedSlides.map((image, index) => {
                        const slideLinkHref = resolveSlideLink(image.link);
                        const hasLink = slideLinkHref !== '#';
                        const slideImageSrc = selectSlideImageSrc(image, heroImageSize);
                        // Tüm slayt görselleri DOM’da; ViewportImage defer Swiper’da IO tetiklemez → siyah slayt

                        const slideOverlayContent = activeIndex === index && (
                            <motion.div 
                                key={`slide-content-${index}`}
                                variants={containerVariants}
                                initial={false}
                                animate="visible"
                                exit="hidden"
                                className="pointer-events-auto w-full max-w-[min(100%,48rem)] px-3 text-center text-white sm:px-5"
                            >
                                {/* Modal: Küçük başlık → description (üst, kırmızı); Büyük başlık → title (ana, beyaz) */}
                                {renderTitle(
                                    image.description ?? '',
                                    undefined,
                                    undefined,
                                    `slide-${index}-sub`,
                                    'small',
                                    'description'
                                )}
                                {renderTitle(
                                    image.title ?? '',
                                    image.highlightWord,
                                    image.element,
                                    `slide-${index}`,
                                    'large',
                                    'title'
                                )}
                                {image.alt?.trim() ? (
                                    <motion.p
                                        variants={descriptionVariants}
                                        className="mx-auto mb-1 mt-1.5 max-w-2xl px-1 text-xs leading-relaxed text-white/90 sm:mt-2 sm:px-2 sm:text-sm md:mt-3 md:text-base"
                                        data-prop-path="alt"
                                        data-prop-allow-html="false"
                                    >
                                        {image.alt}
                                    </motion.p>
                                ) : null}
                                {(() => {
                                    const slideFeatures = Array.isArray(image.features) && image.features.length > 0 ? image.features : fallbackFeatures;
                                    return (slideFeatures.slice(0, 5).length > 0) && (
                                    <motion.div 
                                        variants={descriptionVariants}
                                        className="mt-3 flex flex-col items-center justify-center gap-2 sm:mt-4 sm:flex-row sm:flex-wrap sm:gap-x-4 sm:gap-y-2 md:mt-6 md:gap-x-6"
                                    >
                                        {slideFeatures.slice(0, 5).map((item, i) => {
                                            const iconSrc = getFeatureIconSrc(item);
                                            return (
                                                <span key={i} className="inline-flex max-w-[min(100%,20rem)] items-center gap-2 text-sm leading-snug text-white sm:max-w-none sm:text-base md:text-lg">
                                                    {iconSrc ? (
                                                        <img src={iconSrc} alt="" className="h-4 w-4 flex-shrink-0 object-contain sm:h-5 sm:w-5" />
                                                    ) : (
                                                        <span className="text-alsero-first font-bold">✔</span>
                                                    )}
                                                    <span>{item.text}</span>
                                                </span>
                                            );
                                        })}
                                    </motion.div>
                                );
                                })()}
                                {hasLink && (
                                    <motion.div
                                        variants={descriptionVariants}
                                        className="mt-4 flex justify-center px-1 sm:mt-6 sm:px-2 md:mt-8"
                                    >
                                        <Link
                                            href={slideLinkHref}
                                            aria-label={getSlideLinkAccessibleName(image)}
                                            className="pointer-events-auto inline-flex min-h-[44px] w-full max-w-xs items-center justify-center rounded-lg bg-alsero-first px-6 py-2.5 text-center text-sm font-semibold text-white transition hover:bg-alsero-first/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:min-h-[48px] sm:w-auto sm:max-w-none sm:px-8 sm:py-3 sm:text-base md:text-lg"
                                        >
                                            {getSlideCtaButtonText(image)}
                                        </Link>
                                    </motion.div>
                                )}
                            </motion.div>
                        );

                        const slideInner = (
                            <>
                                <motion.div className="pointer-events-none relative w-full h-full bg-[#1a1a1a]">
                                    {slideImageSrc ? (
                                    <Image
                                        src={slideImageSrc}
                                        alt={
                                            image.alt?.trim()
                                                ? image.title?.trim() ||
                                                  image.description?.trim() ||
                                                  'Slide image'
                                                : image.alt ?? image.title ?? image.description ?? 'Slide image'
                                        }
                                        fill
                                        viewportDefer={false}
                                        priority={index === 0}
                                        loading={index === 0 ? undefined : 'lazy'}
                                        fetchPriority={index === 0 ? 'high' : 'auto'}
                                        className="h-full object-cover object-[center_28%] sm:object-center"
                                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 100vw, 100vw"
                                        unoptimized={slideImageSrc.startsWith('/api/media/proxy/')}
                                    />
                                    ) : null}
                                </motion.div>
                                <div className="pointer-events-none absolute inset-0 z-10 flex items-end justify-center pb-[max(5.5rem,env(safe-area-inset-bottom,0px))] pt-12 sm:pb-24 sm:pt-16 md:pb-28 md:pt-20 lg:pb-40 lg:pt-24">
                                    <AnimatePresence mode="wait">
                                        {slideOverlayContent}
                                    </AnimatePresence>
                                </div>
                            </>
                        );

                        return (
                            <SwiperSlide key={image.id || `slide-${index}`} className="!pointer-events-none">
                                <div className="relative h-full w-full pointer-events-none [&_.pointer-events-auto]:pointer-events-auto">
                                    {slideInner}
                                </div>
                            </SwiperSlide>
                        );
                    })
                )}
            </Swiper>

            {/* Custom Navigation — swiper üstünde; tam slayt linki kaldırıldığı için tıklanabilir */}
            <motion.div 
                initial={false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.6 }}
                className="pointer-events-auto absolute top-0 bottom-0 items-center right-3 z-50 flex gap-2  sm:right-6 sm:gap-3 md:right-8"
            >
                <motion.button 
                    type="button"
                    onClick={() => swiperInstance?.slidePrev()}
                    className="cursor-pointer rounded-xl border border-white/90 bg-black/15 p-2.5 backdrop-blur-[2px] transition-all duration-300 ease-in-out hover:border-alsero-text-dark/50 hover:bg-alsero-text-dark/50 sm:rounded-2xl sm:p-4 md:p-5"
                    aria-label="Previous slide"
                    whileHover={{ scale: 1.1, x: -5, transition: { duration: 0.2 } }}
                    whileTap={{ scale: 0.95 }}
                >
                    <motion.svg 
                        width="28" 
                        height="25" 
                        viewBox="0 0 28 25" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg" 
                        className="h-5 w-[22px] fill-white sm:h-6 sm:w-7 md:h-[25px] md:w-7"
                        whileHover={{ x: -3 }}
                        transition={{ duration: 0.2 }}
                    >
                        <path d="M0.447544 13.6676L10.5593 24.5202C10.848 24.83 11.2327 25 11.6429 25C12.0536 25 12.438 24.8297 12.7267 24.5202L13.6448 23.5346C13.9332 23.2253 14.0921 22.8122 14.0921 22.3717C14.0921 21.9314 13.9332 21.5044 13.6448 21.1951L7.7457 14.85L26.4873 14.85C27.3323 14.85 28 14.1401 28 13.2329V11.8396C28 10.9325 27.3323 10.1509 26.4873 10.1509L7.67877 10.1509L13.6445 3.77047C13.933 3.46068 14.0919 3.05879 14.0919 2.61829C14.0919 2.17829 13.933 1.77053 13.6445 1.46099L12.7265 0.478606C12.4378 0.168819 12.0533 -8.34465e-07 11.6427 -8.34465e-07C11.2325 -8.34465e-07 10.8478 0.171018 10.5591 0.480805L0.447317 11.3332C0.157987 11.6439 -0.00113344 12.0588 3.33786e-06 12.4998C-0.000906467 12.9422 0.157985 13.3573 0.447544 13.6676Z"/>
                    </motion.svg>
                </motion.button>
                <motion.button 
                    type="button"
                    onClick={() => swiperInstance?.slideNext()}
                    className="cursor-pointer rounded-xl border border-white/90 bg-black/15 p-2.5 backdrop-blur-[2px] transition-all duration-300 ease-in-out hover:border-alsero-text-dark/50 hover:bg-alsero-text-dark/50 sm:rounded-2xl sm:p-4 md:p-5"
                    aria-label="Next slide"
                    whileHover={{ scale: 1.1, x: 5, transition: { duration: 0.2 } }}
                    whileTap={{ scale: 0.95 }}
                >
                    <motion.svg 
                        width="28" 
                        height="25" 
                        viewBox="0 0 28 25" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg" 
                        className="h-5 w-[22px] fill-white sm:h-6 sm:w-7 md:h-[25px] md:w-7"
                        whileHover={{ x: 3 }}
                        transition={{ duration: 0.2 }}
                    >
                        <path d="M27.5525 11.3324L17.4407 0.479833C17.152 0.170044 16.7673 2.93169e-06 16.3571 2.85997e-06C15.9464 2.78816e-06 15.562 0.170288 15.2733 0.479832L14.3552 1.46539C14.0668 1.77469 13.9079 2.18782 13.9079 2.62832C13.9079 3.06857 14.0668 3.49562 14.3552 3.80492L20.2543 10.15L1.51267 10.15C0.667668 10.15 -1.89881e-06 10.8599 -2.05742e-06 11.7671L-2.30103e-06 13.1604C-2.45964e-06 14.0675 0.667667 14.8491 1.51267 14.8491L20.3212 14.8491L14.3555 21.2295C14.067 21.5393 13.9081 21.9412 13.9081 22.3817C13.9081 22.8217 14.067 23.2295 14.3555 23.539L15.2735 24.5214C15.5622 24.8312 15.9467 25 16.3573 25C16.7675 25 17.1522 24.829 17.4409 24.5192L27.5527 13.6668C27.842 13.3561 28.0011 12.9412 28 12.5003C28.0009 12.0578 27.842 11.6427 27.5525 11.3324Z"/>
                    </motion.svg>
                </motion.button>
            </motion.div>
        </section>
    )
}
